import { PlaybackOptimizationConfig } from '../datasource/generated';

/**
 * The exact set of hls.js options JW Player 8.38.10 forwards from `hlsjsConfig`.
 *
 * The bundled provider (`public/jwplayer/provider.hlsjs.js`) picks these keys off the object and
 * discards everything else, so any other hls.js option written here is silently dropped. Keep this
 * list in sync with the provider whenever the vendored JW build is updated.
 */
export const JW_HLSJS_CONFIG_WHITELIST = [
    'liveSyncDuration',
    'liveSyncDurationCount',
    'liveMaxLatencyDuration',
    'liveMaxLatencyDurationCount',
    'liveBackBufferLength',
    'backBufferLength',
    'loader',
    'pLoader',
    'fLoader',
    'fragLoadingMaxRetry',
    'fragLoadingRetryDelay',
    'enableWorker',
    'debug',
] as const;

export type JwHlsjsConfigKey = typeof JW_HLSJS_CONFIG_WHITELIST[number];

export type JwHlsjsConfig = Partial<Record<JwHlsjsConfigKey, unknown>>;

export const defaultPlaybackOptimization: Required<PlaybackOptimizationConfig> = {
    enabled: false,
    backBufferLengthSeconds: 30,
    fragLoadingMaxRetry: 6,
    fragLoadingRetryDelayMs: 1000,
    constrainedBandwidthEstimateBps: 600_000,
    constrainedInitialHeightCap: 480,
    constrainedCapReleaseMs: 15000,
    stallWatchdogThresholdMs: 10000,
    stallWatchdogMaxNudges: 3,
    stallWatchdogNudgeSeconds: 0.1,
    frameFreezeThresholdMs: 5000,
    frameFreezeMaxNudges: 2,
    mountBeforePosterCached: true,
};

export const resolvePlaybackOptimization = (
    config: PlaybackOptimizationConfig | undefined | null,
): Required<PlaybackOptimizationConfig> => {
    if (!config) return defaultPlaybackOptimization;

    const resolved = { ...defaultPlaybackOptimization };

    (Object.keys(defaultPlaybackOptimization) as Array<keyof PlaybackOptimizationConfig>)
        .forEach(key => {
            const value = config[key];
            if (value !== undefined && value !== null) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (resolved as any)[key] = value;
            }
        });

    return resolved;
};

/**
 * Strips every key JW would discard, so a config object never claims to tune something it cannot.
 */
export const pickSupportedHlsjsConfig = (config: Record<string, unknown>): JwHlsjsConfig => {
    const supported: JwHlsjsConfig = {};

    JW_HLSJS_CONFIG_WHITELIST.forEach(key => {
        if (config[key] !== undefined) {
            supported[key] = config[key];
        }
    });

    return supported;
};

export interface NetworkConditions {
    saveData: boolean;
    effectiveType?: string;
    downlinkMbps?: number;
}

interface NetworkInformationLike {
    saveData?: boolean;
    effectiveType?: string;
    downlink?: number;
}

export const readNetworkConditions = (
    nav: Navigator = navigator,
): NetworkConditions | null => {
    const connection = (nav as Navigator & {
        connection?: NetworkInformationLike;
        mozConnection?: NetworkInformationLike;
        webkitConnection?: NetworkInformationLike;
    });

    const info = connection.connection ?? connection.mozConnection ?? connection.webkitConnection;

    if (!info) return null;

    return {
        saveData: Boolean(info.saveData),
        effectiveType: info.effectiveType,
        downlinkMbps: info.downlink,
    };
};

const CONSTRAINED_EFFECTIVE_TYPES = ['slow-2g', '2g', '3g'];
const CONSTRAINED_DOWNLINK_MBPS = 1.5;

export const isConstrainedNetwork = (conditions: NetworkConditions | null): boolean => {
    if (!conditions) return false;

    if (conditions.saveData) return true;

    if (conditions.effectiveType && CONSTRAINED_EFFECTIVE_TYPES.includes(conditions.effectiveType)) {
        return true;
    }

    return typeof conditions.downlinkMbps === 'number'
        && conditions.downlinkMbps > 0
        && conditions.downlinkMbps < CONSTRAINED_DOWNLINK_MBPS;
};

export interface JwPlaybackConfigFragment {
    hlsjsConfig: JwHlsjsConfig;
    /**
     * Omitted entirely on an unconstrained connection — see buildJwPlaybackConfig for why.
     */
    defaultBandwidthEstimate?: number;
}

/**
 * Builds the JW config fragment for the on-demand HLS path.
 *
 * Only two levers survive JW's filtering and actually reach hls.js:
 *  - `hlsjsConfig.backBufferLength`, which bounds the otherwise unbounded back buffer
 *    (hls.js 1.6.5 defaults it to Infinity, so a long program never releases played media);
 *  - the retry pair. JW overrides hls.js's own defaults here, tightening them from 6 retries at a
 *    1 s initial delay down to 2 retries at 4 s, so a brief burst of packet loss turns fatal
 *    unusually fast and the first recovery attempt is four seconds late. These restore upstream.
 *
 * The startup rendition is steered through the top-level `defaultBandwidthEstimate`, which JW
 * forwards as `abrEwmaDefaultEstimate`. That is the supported replacement for the abr*Factor
 * options, which JW discards.
 *
 * That estimate is only set on a connection the browser reports as constrained. JW resolves its
 * starting bandwidth as `bandwidthEstimate` → `defaultBandwidthEstimate` → a value it persists per
 * viewer in localStorage, so setting ours unconditionally would discard a learned estimate on every
 * session. A returning viewer on a fast line would be dragged back to a mid rendition and made to
 * ramp up again, every time. Leaving the key off hands that viewer back to JW's own learned value,
 * and first-time viewers to JW's default — while a constrained connection still gets the safe start
 * it needs.
 */
export const buildJwPlaybackConfig = (
    options: Required<PlaybackOptimizationConfig>,
    conditions: NetworkConditions | null,
): JwPlaybackConfigFragment => {
    const hlsjsConfig = pickSupportedHlsjsConfig({
        backBufferLength: options.backBufferLengthSeconds,
        fragLoadingMaxRetry: options.fragLoadingMaxRetry,
        fragLoadingRetryDelay: options.fragLoadingRetryDelayMs,
    });

    if (!isConstrainedNetwork(conditions)) return { hlsjsConfig };

    return {
        hlsjsConfig,
        defaultBandwidthEstimate: options.constrainedBandwidthEstimateBps,
    };
};

const injectedPreconnectOrigins = new Set<string>();

/**
 * Opens the DNS/TLS handshake to a media origin before the player asks for the manifest.
 *
 * The origin only becomes known once launch details resolve, so this cannot live in index.html.
 * It matters more now that the player mounts immediately: the handshake overlaps the remaining
 * React work instead of landing in front of the first manifest request.
 */
export const preconnectToMediaOrigin = (
    url: string | null | undefined,
    doc: Document = document,
): string | null => {
    if (!url) return null;

    let origin: string;

    try {
        origin = new URL(url, doc.baseURI).origin;
    } catch {
        return null;
    }

    if (origin === doc.location?.origin) return null;
    if (injectedPreconnectOrigins.has(origin)) return origin;

    injectedPreconnectOrigins.add(origin);

    const preconnect = doc.createElement('link');
    preconnect.rel = 'preconnect';
    preconnect.href = origin;
    preconnect.crossOrigin = 'anonymous';

    const dnsPrefetch = doc.createElement('link');
    dnsPrefetch.rel = 'dns-prefetch';
    dnsPrefetch.href = origin;

    doc.head.append(preconnect, dnsPrefetch);

    return origin;
};

/** Test seam: clears the module-level record of origins already hinted. */
export const resetPreconnectCache = () => injectedPreconnectOrigins.clear();

export interface QualityLevelLike {
    height?: number;
    label?: string;
}

/**
 * Index of the highest rendition at or below the configured height cap, or null when the cap does
 * not apply. Index 0 is JW's "Auto" entry and is never selected here.
 */
export const findCappedQualityIndex = (
    levels: QualityLevelLike[] | undefined,
    heightCap: number,
): number | null => {
    if (!levels?.length || heightCap <= 0) return null;

    let bestIndex: number | null = null;
    let bestHeight = 0;

    levels.forEach((level, index) => {
        if (index === 0) return;
        if (typeof level.height !== 'number' || level.height <= 0) return;
        if (level.height > heightCap) return;

        if (level.height > bestHeight) {
            bestHeight = level.height;
            bestIndex = index;
        }
    });

    return bestIndex;
};

/**
 * Decides whether the constrained-connection quality cap's automatic release should fire.
 *
 * Selecting an explicit quality in JW turns adaptive bitrate off, and a viewer manually picking a
 * quality during the cap window does exactly that too. Firing the release unconditionally would
 * silently overwrite that choice back to Auto — undoing viewer intent, and on a genuinely
 * constrained connection, risking the stalling this feature exists to prevent. The release should
 * only run the moment nothing has changed since the cap was applied.
 */
export const shouldReleaseQualityCap = (
    latestQualityIndex: number | null,
    cappedIndex: number,
): boolean => latestQualityIndex === cappedIndex;
